import * as THREE from 'three';
import { shortestAngleDelta } from '@/math/angles';
import { tuning } from '@/config/tuning';
import type { Track } from '@/world/Track';
import type { DifficultyLevel } from '@/config/tuning';
import type { CourseExtras } from '@/config/cities';
import type { InputFrame } from '@/input/InputManager';
import type { RaceFx, RaceLike } from '@/core/RaceLike';
import type { SimCore, SimEvent } from '@/sim/SimCore';
import type { SimRacer } from '@/sim/SimRacer';
import type { NetDriver } from '@/net/NetDriver';
import { isLocallyPredicted } from '@/net/WsDriver';
import { CarView, ballOnNose } from '@/entities/CarView';
import type { CarLook } from '@/entities/carLook';
import { VEHICLES, stockColorOf } from '@/entities/vehicles';
import { BallView } from '@/entities/BallView';
import { PickupsView } from '@/entities/PickupsView';
import { TackleGhost } from '@/entities/TackleGhost';
import { FreeKickWallView } from '@/entities/FreeKickWallView';
import { MudView } from '@/entities/MudView';
import { MoversView } from '@/entities/MoversView';
import { isActive } from '@/physics/powerups';
import {
  sfxBallHit,
  sfxBoost,
  sfxCollect,
  sfxCountdown,
  sfxDeny,
  sfxDozerHit,
  sfxDozerTransform,
  sfxItemLand,
  sfxKick,
  sfxMagnet,
  sfxMud,
  sfxOffside,
  sfxPibe,
  sfxRouletteSpin,
  sfxScorpion,
  sfxShield,
  sfxShieldBlock,
  sfxSpinOut,
  sfxShortcut,
  sfxStuds,
  sfxStudsHit,
  sfxTackleHit,
  sfxTackleLaunch,
  sfxWallBreak,
  sfxWallPlant,
} from '@/audio/sfx';

/**
 * NetRace.ts — the ONLINE client driver (counterpart to the offline Race). It
 * implements the same {@link RaceLike} surface (Game/HUD/camera stay
 * mode-agnostic) and renders from a {@link NetDriver}: the in-tab loopback OR a
 * real server over WebSocket. Your car + the rivals are all PREDICTED forward
 * (so collisions line up); corrections ease in via the entities' render offsets;
 * audio/FX come from the host's reliable event channel.
 */
/** M81: per-slot cosmetic looks for the online field (fed from server data —
 *  the welcome/roster/looks messages; Game owns the store). `key` is the
 *  serialized loadout string, so a changed look is a cheap string compare.
 *
 *  M109: `vehicleId` (the sim's truth for that slot) rides along so the
 *  provider can backstop OUR OWN seat from the local player-look seam when the
 *  server sent nothing — the seam is per-vehicle, so it may only be used for
 *  the ride it was published for. */
export interface NetLookProvider {
  lookKeyFor(slot: number, vehicleId: string): string;
  lookFor(slot: number, vehicleId: string): CarLook | undefined;
}

/** M143: the server's events are sounds and flashes for something that JUST
 *  happened. If the frame loop was not draining them (a hidden tab, a long
 *  freeze) what piled up is history — playing it all on the first frame back
 *  was a wall of stale noise, toasts and camera shake. A batch that waited
 *  longer than this is dropped instead. */
export const EVENT_STALE_MS = 400;

/**
 * The events still worth playing after `gapMs` without a drain (pure —
 * unit-tested in tests/net/netRaceEvents.test.ts).
 *
 * Up to EVENT_STALE_MS: everything. Beyond it only OUR OWN cues survive — the
 * ones our predicted world produced (item fire / deny / collect, ball hit,
 * boost pad, the countdown beeps). Those are never stale: they come from the
 * ticks simulated on this very frame, including the ones re-simulated to make
 * up for the freeze, so a "GO!" that fell inside it is still heard.
 */
export function eventsWorthPlaying(events: SimEvent[], gapMs: number, localId: number): SimEvent[] {
  if (gapMs <= EVENT_STALE_MS) return events;
  return events.filter((e) => isLocallyPredicted(e, localId));
}

export class NetRace implements RaceLike {
  fx: RaceFx = {};

  private readonly views = new Map<
    number,
    { car: CarView; ball: BallView; vehicleId: string; lookKey: string }
  >();
  private readonly pickupsView = new PickupsView();
  private readonly tackleGhost = new TackleGhost();
  private readonly wallsView = new FreeKickWallView();
  private readonly mudView = new MudView();
  private readonly moversView = new MoversView();
  private localPlayerId: number;
  private ghostShown = false;
  /** M143: when the driver's event queue was last drained (see EVENT_STALE_MS). */
  private lastDrainAt = performance.now();

  constructor(
    private readonly scene: THREE.Scene,
    private readonly driver: NetDriver,
    /** Optional (the loopback demo passes none) — slots render stock without it. */
    private readonly looks?: NetLookProvider,
  ) {
    scene.add(this.pickupsView.group);
    scene.add(this.tackleGhost.group);
    scene.add(this.wallsView.group);
    scene.add(this.mudView.group);
    scene.add(this.moversView.group);
    this.pickupsView.setSpots(this.sim.pickups);
    this.localPlayerId = this.driver.player.id;
    this.syncViews();
    this.warpViews();
  }

  /** The client's prediction sim — source of truth for HUD readouts (reconciled
   *  to the host every snapshot). */
  private get sim(): SimCore {
    return this.driver.client.predictor.sim;
  }

  /** M105: the live prediction SimCore, exposed so Game can seed a watchdog
   *  rejoin's fresh driver from the world we are ALREADY rendering (instead of
   *  flashing every car back to the start grid). */
  get predictionSim(): SimCore {
    return this.driver.client.predictor.sim;
  }
  private get track(): Track {
    return this.sim.track;
  }

  /** Lobby/connection status for the HUD (null once the race is live). */
  get netStatus(): string | null {
    return this.driver.status ?? null;
  }

  /** Smoothed link RTT (ms) for the HUD ping pill; null on the loopback. */
  /** M108: when the driver last applied a snapshot (any channel) — feeds the
   *  Game link watchdog alongside the WS-arrival stamp. */
  get lastSnapAtMs(): number | null {
    return this.driver.lastSnapAt ?? null;
  }

  get netRtt(): number | null {
    return this.driver.rtt ?? null;
  }

  /** M143c: the link's best measured round trip, handed to the next driver. */
  get rttFloor(): number | null {
    return this.driver.rttFloor ?? null;
  }

  // --- RaceLike surface ---
  get racers(): SimRacer[] {
    return this.sim.racers;
  }
  get player(): SimRacer {
    return this.driver.player;
  }
  get state(): SimCore['state'] {
    return this.sim.state;
  }
  get countdown(): number {
    return this.sim.countdown;
  }
  get goFlash(): number {
    return this.sim.goFlash;
  }
  get raceTime(): number {
    return this.sim.raceTime;
  }
  standings(): SimRacer[] {
    return this.sim.standings();
  }
  playerPlace(): number {
    // Online: "my" place is the LOCAL player's slot (which may not be slot 0),
    // not the sim's isPlayer (always slot 0). Fixes the HUD showing the host's
    // position to a joining friend.
    return this.sim.standings().findIndex((r) => r.id === this.localPlayerId) + 1;
  }

  /** Loading round: forward a dropped-tick report to the driver. */
  absorbDroppedTicks(n: number): void {
    this.driver.absorbDroppedTicks?.(n);
  }

  /** M143c: bring the world up to the newest snapshot received — called
   *  before a result table is read off it (see WsDriver.flushSnapshot). */
  flushSnapshot(): void {
    this.driver.flushSnapshot?.();
  }

  update(_dt: number, input: InputFrame): void {
    // M143: a FINISHED racer's item button is dead. Touch already hides it,
    // but the keyboard and gamepad are still read during the finish beat and
    // while spectating — and the server would fire a held item (a slide
    // tackle at the leader, say) for a car that has already crossed the line.
    const useItem = input.useItem && !this.driver.player.finished;
    // §6.2: flip screen-space steer into world space before it goes on the wire.
    this.driver.tick({ throttle: input.throttle, steer: -input.steer, useItem });
    this.drainEvents();
  }

  /** M143: the frame loop is about to restart after a pause (the tab was
   *  hidden) — whatever the server sent meanwhile is history. Throw it away
   *  unplayed, and start the staleness clock afresh. */
  clearQueuedEvents(): void {
    this.driver.takeEvents();
    this.lastDrainAt = performance.now();
  }

  private drainEvents(): void {
    const me = this.localPlayerId;
    // M143: events that sat undrained too long are dropped, not played late
    // (see eventsWorthPlaying). Normally this drains every tick (8 ms).
    const now = performance.now();
    const gapMs = now - this.lastDrainAt;
    this.lastDrainAt = now;
    // One tyre screech per drain, however many victims spun this tick (an
    // offside trap can spin the whole field at once — stacked identical
    // squeals would just clip). The loudest requested variant wins by order.
    let screeched = false;
    const spinScreech = (hard: boolean): void => {
      if (screeched) return;
      screeched = true;
      sfxSpinOut(hard);
    };
    for (const e of eventsWorthPlaying(this.driver.takeEvents(), gapMs, me)) {
      switch (e.type) {
        case 'countdown':
          sfxCountdown(e.go);
          break;
        case 'pad':
          if (e.racerId === me) sfxBoost(); // M99: your pad hit = the boost whoosh
          break;
        case 'itemLand':
          if (e.racerId === me) sfxItemLand();
          break;
        case 'collect':
          if (e.racerId === me) {
            sfxCollect();
            sfxRouletteSpin(tuning.powerups.rouletteTime);
          }
          break;
        case 'fire':
          switch (e.kind) {
            case 'boost':
              sfxBoost();
              break;
            case 'magnet':
              sfxMagnet();
              break;
            case 'dozer':
              sfxDozerTransform();
              break;
            case 'shield':
              sfxShield();
              break;
            case 'kick':
              sfxKick();
              if (e.racerId === me) this.fx.onPlayerBallHit?.(0.7);
              this.popKick(e.racerId); // M145: impact burst at the struck ball
              break;
            case 'wall':
              sfxWallPlant();
              break;
            case 'offside':
              sfxOffside();
              this.views.get(e.racerId)?.car.triggerOffside(); // M145: the flag goes up
              break;
            case 'pibe':
              sfxPibe();
              if (e.racerId !== me) this.fx.onPibeLoose?.();
              break;
            case 'studs':
              sfxStuds();
              if (e.racerId !== me) this.fx.onStudsLoose?.();
              break;
            case 'scorpion':
              sfxScorpion();
              break;
            case 'mud':
              sfxMud();
              this.views.get(e.racerId)?.car.triggerMud(); // spray out the firer's back
              break;
          }
          break;
        case 'deny':
          if (e.racerId === me) {
            sfxDeny();
            this.fx.onPlayerDenied?.();
          }
          break;
        case 'ballHit':
          if (e.racerId === me) {
            sfxBallHit(e.intensity);
            this.fx.onPlayerBallHit?.(e.intensity);
          }
          break;
        case 'dozerHit':
          sfxDozerHit();
          spinScreech(true); // M58: the victim's tyres squeal into the spin
          if (e.victimId === me) this.fx.onPlayerDozed?.();
          this.launchDozerTumble(e.victimId);
          break;
        case 'shieldBlock':
          sfxShieldBlock();
          this.flashShields(); // M145: the bubble that took the hit flares white
          break;
        case 'tackleLaunch':
          this.tackleGhost.launch(e.color);
          this.ghostShown = true;
          sfxTackleLaunch();
          if (e.targetId === me) this.fx.onPlayerTackleIncoming?.();
          break;
        case 'tackleHit':
          sfxTackleHit();
          spinScreech(true);
          if (e.victimId === me) this.fx.onPlayerDozed?.();
          break;
        case 'offsideHit':
          spinScreech(false); // M58: the caught pack wobbles with a soft squeal
          if (e.victimId === me) this.fx.onPlayerOffside?.();
          break;
        case 'pibeHit':
          sfxDozerHit();
          spinScreech(true);
          if (e.victimId === me) this.fx.onPlayerDozed?.();
          break;
        case 'clash':
          sfxDozerHit(); // M57: a weight-class hit landed (truck swats bike)
          spinScreech(e.tier === 'spin'); // M58: squeal scaled to the tier
          if (e.victimId === me && e.tier === 'spin') this.fx.onPlayerDozed?.();
          break;
        case 'studsHit':
          sfxStudsHit(); // M59: a rival shredded on the spikes
          spinScreech(true);
          if (e.victimId === me) this.fx.onPlayerDozed?.();
          break;
        case 'wallBreak':
          sfxWallBreak();
          break;
        case 'shortcut':
          sfxShortcut(); // M116: everyone hears a racer vanish into a secret cut
          if (e.racerId === me) this.fx.onPlayerShortcut?.();
          break;
        case 'finish':
          break;
      }
    }
  }

  /** M145: the KICK's impact burst, at the kicker's ball as last drawn. */
  private popKick(racerId: number): void {
    const v = this.views.get(racerId);
    if (!v) return;
    const b = v.ball.mesh.position;
    v.car.triggerKick(b.x, b.y, b.z);
  }

  /** M145: a shield turned a hit away. The event carries no racer, so every
   *  bubble that is up flares — only the one that blocked is ever in a fight. */
  private flashShields(): void {
    for (const r of this.sim.racers) {
      if (isActive(r.powerups, 'shield')) this.views.get(r.id)?.car.triggerShieldBlock();
    }
  }

  render(alpha: number, frameDt: number): void {
    const poses = this.driver.poses(alpha, frameDt);
    for (const pose of poses) {
      let v = this.views.get(pose.id);
      if (!v) continue;
      const r = this.sim.racers.find((x) => x.id === pose.id);
      if (r) {
        // M51: snapshots carry each racer's vehicle (the room's authority —
        // humans' picks + randomized AI rides). Rebuild a stale view in place.
        // M51: rebuild on a vehicle change (roster/snapshot adoption). M81:
        // …or a LOOK change (the welcome/roster/looks messages) — a cheap
        // string compare, so a synced cosmetic always lands on screen.
        if (
          v.vehicleId !== r.vehicleId ||
          v.lookKey !== (this.looks?.lookKeyFor(r.id, r.vehicleId) ?? '')
        ) {
          v = this.rebuildView(r, v);
        }
        v.car.setBoosting(isActive(r.powerups, 'boost') || r.padBoost > 0); // M99: pads flame too
        v.car.setDozer(isActive(r.powerups, 'dozer'));
        v.car.setShield(isActive(r.powerups, 'shield'));
        v.car.setPibe(isActive(r.powerups, 'pibe'));
        v.car.setStunned(r.stunTimer > 0); // M58: dizzy stars + smoke + wobble
        v.car.setStuds(isActive(r.powerups, 'studs')); // M59: spinning spikes
        v.car.setScorpion(r.scorpionTimer); // M60b: two-phase pop-up → flip volley
        // M145: the magnet field, onto the ball as it is DRAWN (the pose).
        v.car.setMagnet(isActive(r.powerups, 'magnet'), pose.ball.x, pose.ball.y, pose.ball.z);
      }
      v.car.setDribbling(ballOnNose(pose.car, pose.ball, v.vehicleId)); // M128: the gloves' grab (M153: the view's ride)
      v.car.update(pose.car, frameDt);
      v.ball.update(pose.ball, frameDt);
    }
    this.pickupsView.update(frameDt, this.sim.pickups);
    this.wallsView.update(this.sim.walls);
    this.mudView.update(this.sim.mud);
    this.moversView.update(this.track, this.sim.movers, this.sim.raceTime);
    const t = this.sim.tackle;
    if (t) {
      // M143: a live tackle always SHOWS its ghost. The ghost used to appear
      // only when the 'tackleLaunch' event played — an event that is now
      // dropped when stale, and that a mid-race rejoin never receives — which
      // would leave the player tackled by something invisible. (Silent here:
      // the launch sound belongs to the event.)
      if (!this.ghostShown) {
        const firer = this.sim.racers.find((x) => x.id === t.firerId);
        this.tackleGhost.launch(firer?.color ?? 0xffffff);
        this.ghostShown = true;
      }
      const s = this.track.sampleAt(t.dist);
      const slide01 = t.phase === 'slide' ? t.timer / tuning.powerups.tackle.slideTime : 0;
      this.tackleGhost.update(
        s.x + s.rightX * t.lat,
        s.y + 0.15,
        s.z + s.rightZ * t.lat,
        t.heading,
        slide01,
      );
    } else if (this.ghostShown) {
      this.tackleGhost.hide();
      this.ghostShown = false;
    }
  }

  reset(): void {
    this.driver.reset();
    this.localPlayerId = this.driver.player.id;
    this.pickupsView.setSpots(this.sim.pickups);
    this.tackleGhost.hide();
    this.wallsView.hide();
    this.mudView.hide();
    this.moversView.hide();
    this.ghostShown = false;
    this.syncViews();
    this.warpViews();
  }

  // Car-select is an offline (Tour / Quick Race) feature — online keeps the
  // server-assigned slot vehicles. No-op, but RaceLike requires it (M35).
  rebuildPlayerView(): void {}

  // Online races don't re-target a city in place (a fresh race = a fresh driver);
  // Game never calls this on a NetRace, but RaceLike requires it.
  loadTrack(
    _t: Track,
    _d: DifficultyLevel,
    _c: CourseExtras | undefined,
    _s: number,
    _f: number,
  ): void {
    this.reset();
  }

  private syncViews(): void {
    for (const [id, v] of this.views) {
      if (!this.sim.racers.some((r) => r.id === id)) {
        this.scene.remove(v.car.group);
        this.scene.remove(v.ball.mesh);
        v.car.dispose(); // M143: free the car's GPU resources (its mod rig), as Race does
        this.views.delete(id);
      }
    }
    for (const r of this.sim.racers) {
      if (this.views.has(r.id)) continue;
      this.views.set(r.id, this.makeView(r));
    }
  }

  /** Build one racer's views from ITS vehicle (M51 — the sim is the truth) and
   *  ITS server-synced cosmetic look (M81 — every client sees your shop gear). */
  private makeView(r: SimRacer): {
    car: CarView;
    ball: BallView;
    vehicleId: string;
    lookKey: string;
  } {
    // M93: stock cars wear the vehicle's garage livery (see Race.makeView) —
    // the slot-coloured BALL is the identity, offline and online alike.
    const car = new CarView(
      stockColorOf(r.vehicleId),
      VEHICLES[r.vehicleId] ?? VEHICLES.sports,
      this.looks?.lookFor(r.id, r.vehicleId),
    );
    const ball = new BallView(r.color, tuning.ball.radius);
    this.scene.add(car.group);
    this.scene.add(ball.mesh);
    return {
      car,
      ball,
      vehicleId: r.vehicleId,
      lookKey: this.looks?.lookKeyFor(r.id, r.vehicleId) ?? '',
    };
  }

  /** Swap a racer's view for its (changed) vehicle/look, in place. */
  private rebuildView(
    r: SimRacer,
    old: { car: CarView; ball: BallView },
  ): { car: CarView; ball: BallView; vehicleId: string; lookKey: string } {
    this.scene.remove(old.car.group);
    this.scene.remove(old.ball.mesh);
    old.car.dispose(); // M128
    const v = this.makeView(r);
    this.views.set(r.id, v);
    v.car.warp(r.car.snapshot(1));
    v.ball.warp(r.ball.snapshot(1));
    return v;
  }

  private warpViews(): void {
    for (const r of this.sim.racers) {
      const v = this.views.get(r.id);
      if (!v) continue;
      v.car.warp(r.car.snapshot(1));
      v.ball.warp(r.ball.snapshot(1));
    }
  }

  dispose(): void {
    for (const v of this.views.values()) {
      this.scene.remove(v.car.group);
      this.scene.remove(v.ball.mesh);
      // M143: free each car's GPU resources (its mod rig). Online builds a NEW
      // NetRace for every race, rematch, tour city and rejoin, so skipping
      // this left a whole field's rigs behind each time. (Ball views share
      // their textures for the whole session — nothing of theirs to free.)
      v.car.dispose();
    }
    this.views.clear();
    this.scene.remove(this.pickupsView.group);
    this.pickupsView.dispose();
    this.scene.remove(this.tackleGhost.group);
    this.tackleGhost.dispose();
    this.scene.remove(this.wallsView.group);
    this.wallsView.dispose();
    this.scene.remove(this.mudView.group);
    this.mudView.dispose();
    this.scene.remove(this.moversView.group);
    this.moversView.dispose();
    this.driver.dispose?.();
  }

  /** M115 (owner) — a bulldozer landed a hit: throw the victim into two flips
   *  and land it square with the road. Presentation only, and triggered by the
   *  'dozerHit' EVENT (which every online client already receives), so this
   *  needs no new synced state and no protocol bump. */
  private launchDozerTumble(victimId: number): void {
    const view = this.views.get(victimId)?.car;
    const r = this.sim.racers.find((x) => x.id === victimId);
    if (!view || !r) return;
    const st = r.car.state;
    const dir = this.track.sampleAt(this.track.project(st.x, st.z).distance).dir;
    view.triggerTumble(tuning.powerups.dozerTumble, shortestAngleDelta(st.heading, dir));
  }
}
